import React, { useContext, useEffect, useState } from "react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ModelTsContext } from "../context/modelTS.context";
import { AppContext } from "../context/app.context";

export const UserChartModal = ({ isOpen, onClose, daysFilter, userFilter }) => {
  // If a specific user is selected, start with 'activity' tab since 'users' tab is not meaningful
  const [activeTab, setActiveTab] = useState(userFilter && userFilter !== 'all' ? 'activity' : 'users');
  const [isChartReady, setIsChartReady] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [activityViewMode, setActivityViewMode] = useState('top');
  const [selectedUser, setSelectedUser] = useState(null);
  const [activityPage, setActivityPage] = useState(1);
  const [copyFeedback, setCopyFeedback] = useState('');

  const context = useContext(ModelTsContext);
  const appContext = useContext(AppContext);

  const {
    logsChartDataResult,
    logsChartDataFetching,
    awsgwGetLogsChartData,
    logsActivityTimeResult,
    logsActivityTimeFetching,
    awsgwGetLogsUserActivityTime,
    setShouldFetchLogsChartData,
    setShouldFetchLogsActivityTime
  } = context || {};

  const { darkMode } = appContext || {};
  const isDarkMode = darkMode || false;

  // Fetch chart data when modal opens or tab changes
  useEffect(() => {
    if (isOpen && !hasFetched) {
      // Set appropriate tab based on user filter BEFORE fetching
      const correctTab = userFilter && userFilter !== 'all' ? 'activity' : 'users';
      setActiveTab(correctTab);
      setIsChartReady(false);
      setHasFetched(true);
      if (correctTab === 'users' && awsgwGetLogsChartData) {
        awsgwGetLogsChartData();
      } else if (correctTab === 'activity' && awsgwGetLogsUserActivityTime) {
        if (activityViewMode === 'specific' && selectedUser) {
          awsgwGetLogsUserActivityTime({ specificUser: selectedUser });
        } else {
          awsgwGetLogsUserActivityTime({
            topN: 10,
            skip: 0,
            sortOrder: activityViewMode === 'top' ? 'desc' : 'asc'
          });
        }
      }
    } else if (!isOpen) {
      setHasFetched(false);
      // Disable queries when modal closes to stop polling
      if (setShouldFetchLogsChartData) setShouldFetchLogsChartData(false);
      if (setShouldFetchLogsActivityTime) setShouldFetchLogsActivityTime(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hasFetched]);

  // Refetch when tab, activity view mode, or date filter changes
  useEffect(() => {
    if (isOpen && hasFetched) {
      setIsChartReady(false);
      if (activeTab === 'users' && awsgwGetLogsChartData) {
        awsgwGetLogsChartData();
      } else if (activeTab === 'activity' && awsgwGetLogsUserActivityTime) {
        if (activityViewMode === 'specific' && selectedUser) {
          awsgwGetLogsUserActivityTime({ specificUser: selectedUser });
        } else {
          const skip = (activityPage - 1) * 10;
          awsgwGetLogsUserActivityTime({
            topN: 10,
            skip: skip,
            sortOrder: activityViewMode === 'top' ? 'desc' : 'asc',
            specificUser: null  // Explicitly clear specific user
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activityViewMode, selectedUser, activityPage, daysFilter]);

  // Wait for data to load and add small delay for DOM to be ready
  useEffect(() => {
    const hasUserData = logsChartDataResult && logsChartDataResult.length > 0 && !logsChartDataFetching;
    const hasActivityData = logsActivityTimeResult && logsActivityTimeResult.length > 0 && !logsActivityTimeFetching;

    if ((activeTab === 'users' && hasUserData) || (activeTab === 'activity' && hasActivityData)) {
      const timer = setTimeout(() => {
        setIsChartReady(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [logsChartDataResult, logsChartDataFetching, logsActivityTimeResult, logsActivityTimeFetching, activeTab, activityPage]);

  const chartData = logsChartDataResult || [];
  const activityData = logsActivityTimeResult || [];

  // Theme colors based on dark mode
  const theme = {
    background: isDarkMode ? '#404040' : '#ffffff',
    text: isDarkMode ? '#ffffff' : '#000000',
    textSecondary: isDarkMode ? '#d1d5db' : '#666666',
    border: isDarkMode ? '#525252' : '#dddddd',
    tabActive: isDarkMode ? '#3b82f6' : '#2563eb',
    tabInactive: isDarkMode ? '#9ca3af' : '#666666',
    buttonBg: isDarkMode ? '#1f2937' : '#ffffff',
    buttonBorder: isDarkMode ? '#525252' : '#cccccc',
    buttonDisabled: isDarkMode ? '#374151' : '#cccccc',
    inputBg: isDarkMode ? '#374151' : '#ffffff',
    overlayBg: isDarkMode ? 'rgba(0, 0, 0, 0.75)' : 'rgba(0, 0, 0, 0.5)',
    chartGrid: isDarkMode ? '#525252' : '#e5e7eb',
    tooltipBg: isDarkMode ? '#374151' : '#ffffff',
    tooltipBorder: isDarkMode ? '#525252' : '#cccccc',
    loadingSpinner: isDarkMode ? '#3b82f6' : '#2563eb'
  };

  // Helper function to shorten email addresses for display
  const shortenEmail = (email) => {
    if (!email) return email;
    // Remove @kellerinteriors.com and other common domains
    let shortened = email.replace(/@kellerinteriors\.com$/i, '')
                         .replace(/@gmail\.com$/i, '')
                         .replace(/@[^@]+$/i, ''); // Remove any other domain
    // Truncate to 12 characters if still too long
    if (shortened.length > 12) {
      shortened = shortened.substring(0, 12) + '...';
    }
    return shortened;
  };

  // Create display version of activity data with shortened usernames
  const activityDataDisplay = activityData.map(item => ({
    ...item,
    user: shortenEmail(item.user),
    originalUser: item.user // Keep original for reference
  }));

  const availableUsers = chartData.length > 0
    ? chartData.flatMap(d => d.userList || [])
      .filter((v, i, a) => a.indexOf(v) === i)
      .sort()
    : [];

  if (!isOpen) return null;
  if (!context) {
    console.error('UserChartModal: ModelTsContext is not available');
    return null;
  }

  const handleCopyUsers = async (date, userList) => {
    // Calculate missing users (in total unique users but not on this day)
    const missingUsers = [...availableUsers].filter(user => !userList.includes(user)).sort();

    // Get the date range from chartData for context
    const dates = chartData.map(d => d.date).sort();
    const dateRange = dates.length > 1
      ? `${dates[0]} to ${dates[dates.length - 1]}`
      : dates[0] || 'selected period';

    // Build the text with active users and missing users sections (copy array before sorting to avoid mutating read-only state)
    let text = `${date}\n\nActive Users (${userList.length}):\n${[...userList].sort().join('\n')}`;
    if (missingUsers.length > 0) {
      text += `\n\n---\n\nMissing Users (${missingUsers.length}) - active during ${dateRange} but not on ${date}:\n${missingUsers.join('\n')}`;
    }

    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        setCopyFeedback('Copied!');
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const success = document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopyFeedback(success ? 'Copied!' : 'Copy failed');
      }
    } catch (err) {
      console.error('Copy failed:', err);
      setCopyFeedback('Copy failed');
    }
    setTimeout(() => setCopyFeedback(''), 2000);
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{
          backgroundColor: theme.tooltipBg,
          padding: '12px',
          border: `1px solid ${theme.tooltipBorder}`,
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}>
          <p style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px', color: theme.text }}>{data.date}</p>
          <p style={{ fontSize: '14px', marginBottom: '8px', color: '#8884d8' }}>
            Unique Users: {data.uniqueUsers}
          </p>
          <div style={{ fontSize: '12px', color: theme.textSecondary, maxHeight: '100px', overflowY: 'auto' }}>
            <p style={{ fontWeight: '600', marginBottom: '4px' }}>Users:</p>
            {data.userList.slice(0, 5).map((user, idx) => (
              <div key={idx}>{user}</div>
            ))}
            {data.userList.length > 5 && (
              <div style={{ fontStyle: 'italic' }}>...and {data.userList.length - 5} more</div>
            )}
          </div>
          <p style={{
            fontSize: '11px',
            color: theme.tabActive,
            marginTop: '8px',
            fontStyle: 'italic',
            borderTop: `1px solid ${theme.border}`,
            paddingTop: '6px'
          }}>
            Click data point to copy user list
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomActivityTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const fullUsername = data.originalUser || data.user;

      return (
        <div style={{
          backgroundColor: theme.tooltipBg,
          padding: '12px',
          border: `1px solid ${theme.tooltipBorder}`,
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}>
          <p style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px', color: theme.text }}>
            {fullUsername || 'No user'}
          </p>
          {data.date && (
            <p style={{ fontSize: '13px', color: theme.textSecondary, marginBottom: '4px' }}>
              {data.date}
            </p>
          )}
          <p style={{ fontSize: '14px', color: '#82ca9d', fontWeight: '500' }}>
            Active Minutes: {Math.round(data.estimatedMinutes)}
          </p>
          <p style={{ fontSize: '12px', color: theme.textSecondary, marginTop: '4px' }}>
            Log Count: {data.logCount}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: theme.overlayBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: theme.background,
          borderRadius: '8px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          padding: '24px',
          maxWidth: '1280px',
          width: '100%',
          margin: '0 16px'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: theme.text }}>
            User Analytics
            {daysFilter && daysFilter !== 'all' && (
              <span style={{ fontSize: '14px', fontWeight: 'normal', color: theme.textSecondary, marginLeft: '8px' }}>
                (Last {daysFilter} {daysFilter === '1' ? 'Day' : 'Days'})
              </span>
            )}
          </h2>
          <button
            onClick={onClose}
            style={{
              color: theme.textSecondary,
              fontSize: '36px',
              lineHeight: '1',
              border: 'none',
              background: 'none',
              cursor: 'pointer'
            }}
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${theme.border}`, marginBottom: '16px' }}>
          {/* Hide "Unique Users" tab when a specific user is filtered - not meaningful to show unique count for one user */}
          {(!userFilter || userFilter === 'all') && (
            <button
              style={{
                padding: '8px 16px',
                fontWeight: '600',
                color: activeTab === 'users' ? theme.tabActive : theme.tabInactive,
                borderTop: 'none',
                borderLeft: 'none',
                borderRight: 'none',
                borderBottom: activeTab === 'users' ? `2px solid ${theme.tabActive}` : 'none',
                background: 'none',
                cursor: 'pointer'
              }}
              onClick={() => setActiveTab('users')}
            >
              Unique Users
            </button>
          )}
          <button
            style={{
              padding: '8px 16px',
              fontWeight: '600',
              color: activeTab === 'activity' ? theme.tabActive : theme.tabInactive,
              borderTop: 'none',
              borderLeft: 'none',
              borderRight: 'none',
              borderBottom: activeTab === 'activity' ? `2px solid ${theme.tabActive}` : 'none',
              background: 'none',
              cursor: 'pointer'
            }}
            onClick={() => setActiveTab('activity')}
          >
            User Activity Time
          </button>
        </div>

        {/* Activity Tab Controls */}
        {activeTab === 'activity' && (
          <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '14px', fontWeight: '600', color: theme.text }}>View:</label>
              <select
                style={{
                  backgroundColor: theme.inputBg,
                  border: `1px solid ${theme.buttonBorder}`,
                  fontSize: '13px',
                  padding: '6px 32px 6px 8px',
                  borderRadius: '4px',
                  color: theme.text,
                  minWidth: '180px',
                  height: '32px'
                }}
                value={activityViewMode}
                onChange={(e) => {
                  const newMode = e.target.value;
                  setActivityViewMode(newMode);
                  setActivityPage(1); // Reset to page 1 when changing mode
                  if (newMode !== 'specific') {
                    setSelectedUser(null);
                    // Refetch data for all users when switching back
                    if (awsgwGetLogsUserActivityTime) {
                      awsgwGetLogsUserActivityTime({
                        topN: 10,
                        skip: 0,
                        sortOrder: newMode === 'top' ? 'desc' : 'asc',
                        specificUser: null  // Explicitly clear specific user
                      });
                    }
                  }
                }}
              >
                <option value="top">Top 10 Most Active</option>
                <option value="bottom">Top 10 Least Active</option>
                <option value="specific">Specific User</option>
              </select>
            </div>
            {activityViewMode === 'specific' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '14px', fontWeight: '600', color: theme.text }}>User:</label>
                <select
                  style={{
                    backgroundColor: theme.inputBg,
                    border: `1px solid ${theme.buttonBorder}`,
                    fontSize: '13px',
                    padding: '6px 32px 6px 8px',
                    borderRadius: '4px',
                    color: theme.text,
                    minWidth: '180px',
                    height: '32px'
                  }}
                  value={selectedUser || ''}
                  onChange={(e) => setSelectedUser(e.target.value)}
                >
                  <option value="">Select a user...</option>
                  {availableUsers.map((user) => (
                    <option key={user} value={user}>{user}</option>
                  ))}
                </select>
              </div>
            )}
            {activityViewMode !== 'specific' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  style={{
                    backgroundColor: activityPage > 1 ? theme.tabActive : theme.buttonDisabled,
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '6px 12px',
                    fontSize: '13px',
                    cursor: activityPage > 1 ? 'pointer' : 'not-allowed',
                    fontWeight: '600'
                  }}
                  onClick={() => {
                    if (activityPage > 1) {
                      setActivityPage(activityPage - 1);
                    }
                  }}
                  disabled={activityPage === 1}
                >
                  ← Previous
                </button>
                <span style={{ fontSize: '13px', fontWeight: '600', color: theme.text }}>
                  Page {activityPage}
                </span>
                <button
                  style={{
                    backgroundColor: activityData.length === 10 ? theme.tabActive : theme.buttonDisabled,
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '6px 12px',
                    fontSize: '13px',
                    cursor: activityData.length === 10 ? 'pointer' : 'not-allowed',
                    fontWeight: '600'
                  }}
                  onClick={() => {
                    if (activityData.length === 10) {
                      setActivityPage(activityPage + 1);
                    }
                  }}
                  disabled={activityData.length < 10}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        )}

        {/* Chart */}
        <div style={{ width: '100%', height: '500px', minHeight: '500px' }}>
          {((activeTab === 'users' && logsChartDataFetching) || (activeTab === 'activity' && logsActivityTimeFetching) || !isChartReady) ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  display: 'inline-block',
                  width: '48px',
                  height: '48px',
                  border: `4px solid ${theme.loadingSpinner}`,
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginBottom: '16px'
                }}></div>
                <p style={{ color: theme.textSecondary }}>Loading chart data...</p>
              </div>
            </div>
          ) : activeTab === 'users' && chartData.length > 0 ? (
            <>
              {/* Copy feedback toast */}
              {copyFeedback && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: copyFeedback === 'Copied!' ? '#22c55e' : '#ef4444',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '14px',
                  zIndex: 100,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}>
                  {copyFeedback}
                </div>
              )}
              <ResponsiveContainer width="100%" height={500} minWidth={300}>
                <LineChart
                  data={chartData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  onClick={(e) => {
                    console.log('Chart clicked:', e);
                    if (e && e.activePayload && e.activePayload.length > 0) {
                      const data = e.activePayload[0].payload;
                      console.log('Copying users for date:', data.date, 'userList:', data.userList);
                      if (data.userList && data.userList.length > 0) {
                        handleCopyUsers(data.date, data.userList);
                      }
                    }
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.chartGrid} />
                  <XAxis
                    dataKey="date"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    tick={{ fontSize: 12, fill: theme.text }}
                    stroke={theme.text}
                  />
                  <YAxis
                    label={{ value: 'Unique Users', angle: -90, position: 'insideLeft', fill: theme.text }}
                    allowDecimals={false}
                    tick={{ fill: theme.text }}
                    stroke={theme.text}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ color: theme.text }} />
                  <Line
                    type="monotone"
                    dataKey="uniqueUsers"
                    stroke="#8884d8"
                    strokeWidth={2}
                    dot={{
                      r: 6,
                      style: { cursor: 'pointer' },
                      onClick: (data) => {
                        console.log('Dot clicked directly:', data);
                        if (data && data.payload) {
                          console.log('Dot payload:', data.payload);
                          if (data.payload.userList && data.payload.userList.length > 0) {
                            handleCopyUsers(data.payload.date, data.payload.userList);
                          }
                        }
                      }
                    }}
                    activeDot={{
                      r: 10,
                      style: { cursor: 'pointer' },
                      onClick: (_, payload) => {
                        console.log('ActiveDot clicked:', payload);
                        if (payload && payload.payload) {
                          const data = payload.payload;
                          console.log('ActiveDot data:', data);
                          if (data.userList && data.userList.length > 0) {
                            handleCopyUsers(data.date, data.userList);
                          }
                        }
                      }
                    }}
                    name="Unique Users"
                    isAnimationActive={false}
                  />
              </LineChart>
            </ResponsiveContainer>
            </>
          ) : activeTab === 'activity' && activityData.length > 0 ? (
            <ResponsiveContainer width="100%" height={500} minWidth={300}>
              <BarChart
                data={activityDataDisplay}
                margin={{ top: 5, right: 30, left: 20, bottom: activityViewMode === 'specific' ? 100 : 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={theme.chartGrid} />
                <XAxis
                  dataKey={activityViewMode === 'specific' ? 'date' : 'user'}
                  angle={activityViewMode === 'specific' ? -45 : 0}
                  textAnchor={activityViewMode === 'specific' ? 'end' : 'middle'}
                  height={activityViewMode === 'specific' ? 100 : 50}
                  tick={{ fontSize: 12, fill: theme.text }}
                  stroke={theme.text}
                />
                <YAxis
                  label={{ value: 'Estimated Active Minutes', angle: -90, position: 'insideLeft', fill: theme.text }}
                  allowDecimals={false}
                  tick={{ fill: theme.text }}
                  stroke={theme.text}
                />
                <Tooltip content={<CustomActivityTooltip />} />
                <Legend wrapperStyle={{ color: theme.text }} />
                <Bar
                  dataKey="estimatedMinutes"
                  fill="#82ca9d"
                  name="Active Minutes (est.)"
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: theme.textSecondary }}>
              {activeTab === 'activity' && activityViewMode === 'specific' && !selectedUser
                ? 'Please select a user to view their activity'
                : 'No data available for chart'}
            </div>
          )}
        </div>

        {/* Summary Stats */}
        {activeTab === 'users' && chartData.length > 0 && (
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${theme.border}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-around', fontSize: '14px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: '600', color: theme.textSecondary }}>Total Days</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.text }}>{chartData.length}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: '600', color: theme.textSecondary }}>Avg Users/Day</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.text }}>
                  {(chartData.reduce((sum, d) => sum + d.uniqueUsers, 0) / chartData.length).toFixed(1)}
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: '600', color: theme.textSecondary }}>Peak Users</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.text }}>
                  {Math.max(...chartData.map(d => d.uniqueUsers))}
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: '600', color: theme.textSecondary }}>Total Unique Users</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.text }}>
                  {chartData.length > 0
                    ? new Set(chartData.flatMap(d => d.userList || [])).size
                    : 0}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Activity Summary Stats */}
        {activeTab === 'activity' && activityData.length > 0 && (
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${theme.border}` }}>
            <div style={{ fontSize: '12px', color: theme.textSecondary, marginBottom: '8px' }}>
              <strong>Note:</strong> Time estimates use actual elapsed time from polling sessions for accuracy. Non-polling logs (initial loads) use 5 minutes per entry.
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-around', fontSize: '14px' }}>
              {activityViewMode === 'specific' ? (
                <>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: '600', color: theme.textSecondary }}>Total Days</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.text }}>{activityData.length}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: '600', color: theme.textSecondary }}>Total Est. Minutes</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.text }}>
                      {Math.round(activityData.reduce((sum, d) => sum + d.estimatedMinutes, 0))}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: '600', color: theme.textSecondary }}>Avg Minutes/Day</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.text }}>
                      {Math.round(activityData.reduce((sum, d) => sum + d.estimatedMinutes, 0) / activityData.length)}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: '600', color: theme.textSecondary }}>Peak Day</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.text }}>
                      {Math.round(Math.max(...activityData.map(d => d.estimatedMinutes)))} min
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: '600', color: theme.textSecondary }}>Users Shown</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.text }}>{activityData.length}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: '600', color: theme.textSecondary }}>Total Est. Minutes</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.text }}>
                      {Math.round(activityData.reduce((sum, d) => sum + d.estimatedMinutes, 0))}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: '600', color: theme.textSecondary }}>Avg per User</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.text }}>
                      {Math.round(activityData.reduce((sum, d) => sum + d.estimatedMinutes, 0) / activityData.length)} min
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
